import CategorySwiper from '@components/category-swiper/CategorySwiper';
import {
    ContentBlockFragment,
    DiscoverMainContentFolderFragment,
    SubCategoryFragment,
} from '@graphqlTypes/sitecore';
import { EffectCoverflow, Pagination, Autoplay } from 'swiper';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import {
    blockTypeLinkTo,
    contentArr,
    isDeepLinkShareUrl,
    isShareUrl,
    isTimeFrame,
    isVideoPop,
    objFilter,
    removeCIDUrl,
    videoNative,
    withWebpImage,
} from '@src/utils/util';
import { withBasePath } from '@src/common/path-convert';
import { jsBridge } from '@src/utils/js-bridge';
import { useGlobalStore } from '@src/hooks/use-global-store';
import isSharePageStore from '@mobx/stores/isShare-store';
import cn from 'classnames';
import useWindowSize from '@src/utils/useWindowSize';
import { isHeroBannerShow } from '@src/common/filter';

type Props = {
    content: DiscoverMainContentFolderFragment | null | undefined;
    filterValue: string;
};

/** Hero banner */
export default function TopBanner({ content, filterValue }: Props) {
    const router = useRouter();
    const { width } = useWindowSize();
    const { settingStore, videoPopupStore } = useGlobalStore();
    const isSharePage = isSharePageStore.isSharePage;

    const isDesktop = width >= 1024;

    const filteredBlocks: ContentBlockFragment[] = useMemo(() => {
        if (!content) return [];
        const resData = contentArr(filterValue, content);
        const herobanner = isHeroBannerShow(resData);
        // console.log('herobanner', herobanner);
        return herobanner;
    }, [content, filterValue]);

    const swiperOptions = useMemo(
        () => ({
            modules: [Pagination, EffectCoverflow, Autoplay],
            pagination: { enabled: true, type: 'bullets' as const },
            initialSlide: 0,
            autoplay: { delay: 4000, disableOnInteraction: false },
            loop: filteredBlocks.length > 3,
            centeredSlides: true,
            updateOnWindowResize: true,
            observer: true,
            observeParents: true,
            resizeObserver: true,
            slidesPerView: 'auto' as const,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onResize: (swiper: any) => swiper.autoplay.start(),
        }),
        [filteredBlocks.length]
    );

    const handleShare = (e: React.MouseEvent, item: ContentBlockFragment) => {
        e.stopPropagation();

        const shareLink =
            item.blockType?.value === 'Internal'
                ? item.internalLink?.value ?? ''
                : `${item?.parentKey?.name}/${item.key}/`;

        const url =
            item.blockType?.value === 'DeepLink'
                ? isDeepLinkShareUrl(item.deepLink?.value ?? '')
                : isShareUrl(item.blockType?.value, shareLink, item.raceLink?.targetItem?.path);

        jsBridge.appShareUrl(url, item);
    };

    const handleContentClick = (item: ContentBlockFragment, index: number) => {
        videoNative(item, jsBridge);

        if (item.blockType?.value === 'Video') {
            videoPopupStore.updateContentBlockData(item);
            return;
        }

        const generalLink = removeCIDUrl(
            `/${item.parentKey?.name}/${item.key}${location.search}`,
            false,
            { isHeroBanner: true, slotNo: index + 1 }
        );
        // console.log('generalLink---', generalLink);
        blockTypeLinkTo(item, generalLink, router, jsBridge, settingStore);
    };

    if (filteredBlocks.length === 0) return null;

    return (
        <CategorySwiper
            options={swiperOptions}
            spaceBetween={isDesktop ? 8 : 0}
            swiperStylesName='banner-swiper'
            swiperClassName='md:h-[324px] h-[226px]'
            slideClassName={cn(isDesktop && '!w-[70vw] max-w-[836px]')}
        >
            {filteredBlocks.map((item, index: number) => {
                const isDeepLink = item.blockType?.value === 'DeepLink';
                const showIcon = item.shareIcon?.boolValue;
                const showTimeIcon = isTimeFrame(
                    item.isIconDisplay?.boolValue ?? false,
                    item.iconDisplayStartDate?.dateValue || 0,
                    item.iconDisplayEndDate?.dateValue || 0
                );
                const heroImg = item.heroBannerImg?.src
                    ? item.heroBannerImg?.src ?? ''
                    : item?.heroImage?.src ?? '';

                const categoryTitle =
                    (item?.category?.categories as SubCategoryFragment[])?.length > 0
                        ? objFilter(item?.category?.categories)[0]?.categoryTitle?.value || ''
                        : '';

                return (
                    <div
                        key={item.key}
                        className={cn('h-full', isSharePage && 'pointer-events-none')}
                    >
                        {isDeepLink ? (
                            // DeepLink 类型：外链跳转
                            <div
                                className={cn(
                                    'relative h-full lg:w-[70vw] lg:overflow-hidden lg:rounded-[8px] bg-cover bg-center',
                                    isDesktop && 'max-w-[836px]'
                                )}
                            >
                                {showIcon && (
                                    <img
                                        className='w-[24px] absolute top-[8px] right-[8px] z-10'
                                        src={withBasePath('/images/share_bg_bule.svg')}
                                        alt='share'
                                        loading='lazy'
                                        onClick={(e) => handleShare(e, item)}
                                    />
                                )}

                                <a
                                    href={item.deepLink?.value}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    <div
                                        className='relative h-full bg-cover bg-center lg:rounded-[8px] lg:overflow-hidden'
                                        style={{
                                            backgroundImage: `url(${withWebpImage(heroImg)})`,
                                        }}
                                    >
                                        {showTimeIcon && (
                                            <img
                                                src={item.iconDisplay?.targetItem?.iconImage?.src}
                                                alt=''
                                                loading='lazy'
                                                className='h-[20px] absolute top-[12px] left-[12px]'
                                            />
                                        )}

                                        <div className='absolute inset-x-0 bottom-0 p-[12px] bg-gradient-to-t from-black text-white'>
                                            {categoryTitle && (
                                                <div
                                                    className='text-[13px] categoryBr line-clamp-2'
                                                    dangerouslySetInnerHTML={{
                                                        __html: categoryTitle,
                                                    }}
                                                />
                                            )}
                                            <div
                                                className='text-[15px]'
                                                dangerouslySetInnerHTML={{
                                                    __html: item.pageTitle?.value || '',
                                                }}
                                            />
                                        </div>
                                    </div>
                                </a>
                            </div>
                        ) : (
                            // 普通内容块：点击跳转或播放视频
                            <div
                                className={cn(
                                    'relative h-full lg:w-[70vw] lg:overflow-hidden lg:rounded-[8px] bg-cover bg-center',
                                    isDesktop && 'max-w-[836px]',
                                    isVideoPop(item)
                                )}
                                style={{
                                    backgroundImage: `url(${withWebpImage(heroImg)})`,
                                }}
                                onClick={() => handleContentClick(item, index)}
                                data-poster={item.videoLink?.value}
                                data-image={item.videoPoster?.src}
                            >
                                {showIcon && (
                                    <img
                                        className='w-[24px] absolute top-[8px] right-[8px] z-10'
                                        src={withBasePath('/images/share_bg_bule.svg')}
                                        alt='share'
                                        loading='lazy'
                                        onClick={(e) => handleShare(e, item)}
                                    />
                                )}

                                {showTimeIcon && (
                                    <img
                                        src={item.iconDisplay?.targetItem?.iconImage?.src}
                                        alt=''
                                        loading='lazy'
                                        className='h-[20px] absolute top-[12px] left-[12px]'
                                    />
                                )}

                                <div className='absolute inset-x-0 bottom-0 p-[12px] bg-gradient-to-t from-black text-white'>
                                    {categoryTitle && (
                                        <div
                                            className='text-[13px] categoryBr line-clamp-2'
                                            dangerouslySetInnerHTML={{ __html: categoryTitle }}
                                        />
                                    )}
                                    <div
                                        className='text-[15px]'
                                        dangerouslySetInnerHTML={{
                                            __html: item.pageTitle?.value || '',
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </CategorySwiper>
    );
}
